"use client";

import { asText } from "@prismicio/client";
import { useEffect, useState } from "react";
import { getPropertyInfoItems } from "./getPropertyInfoItems";
import PicturesDesktop from "./PicturesDesktop";
import PicturesMobile from "./PicturesMobile";
import PropertyInfoCard from "./PropertyInfoCard";
import "./property-slider.css";

const formatFrenchThousands = (value) => {
  const normalizedValue = String(value ?? "").trim();
  if (!normalizedValue) {
    return "";
  }

  const numericValue = Number(normalizedValue.replace(/[\s\u00A0\u202F]/g, "").replace(",", "."));
  if (!Number.isFinite(numericValue)) {
    return normalizedValue;
  }

  return new Intl.NumberFormat("fr-FR").format(numericValue);
};

const PropertyHero = ({ pictures, is_exclusivity, state, date_sold, date_rented, property_type, title, district, city, price, room_numbers, surface, exposition, outside, floor, parking, type }) => {
  const [isShareSuccessVisible, setIsShareSuccessVisible] = useState(false);
  const propertyTitle = asText(title);
  const propertyDistrict = district?.trim();
  const propertyCity = city?.trim();
  const propertyLocation = [propertyDistrict, propertyCity].filter(Boolean).join(", ");
  const propertyPrice = price?.trim?.() || String(price || "").trim();
  const propertyPriceFormatted = formatFrenchThousands(propertyPrice);
  const propertyRoomNumbers = room_numbers?.trim?.() || String(room_numbers || "").trim();
  const propertySurface = surface?.trim?.() || String(surface || "").trim();
  const propertyExposition = exposition?.trim?.() || String(exposition || "").trim();
  const propertyOutside = outside?.trim?.() || String(outside || "").trim();
  const propertyFloor = floor?.trim?.() || String(floor || "").trim();
  const propertyParking = parking?.trim?.() || String(parking || "").trim();
  const propertyInfoItems = getPropertyInfoItems({
    price: propertyPrice,
    priceFormatted: propertyPriceFormatted,
    roomNumbers: propertyRoomNumbers,
    surface: propertySurface,
    exposition: propertyExposition,
    outside: propertyOutside,
    floor: propertyFloor,
    parking: propertyParking,
  });
  const propertyInfoCount = propertyInfoItems.length;
  const propertyInfoCardClass =
    type === "property_rental"
      ? "bg-terracota-100 text-terracota-950"
      : "bg-bleu-100 text-bleu-950";
  const propertyInfoDesktopGridClass = {
    1: "lg:grid-cols-1",
    2: "lg:grid-cols-2",
    3: "lg:grid-cols-3",
    4: "lg:grid-cols-4",
    5: "lg:grid-cols-5",
    6: "lg:grid-cols-6",
    7: "lg:grid-cols-7",
  }[propertyInfoCount] || "lg:grid-cols-1";

  const copyToClipboard = async (value) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  };

  useEffect(() => {
    if (!isShareSuccessVisible) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setIsShareSuccessVisible(false);
    }, 2000);

    return () => window.clearTimeout(timeoutId);
  }, [isShareSuccessVisible]);

  const handleShareClick = async () => {
    const currentUrl = new URL(window.location.href);
    const shareUrl = `${currentUrl.origin}${currentUrl.pathname}`;
    const shareText = [propertyTitle, propertyLocation].filter(Boolean).join(" - ") || "Regardez ce bien !";

    const shareData = {
      title: propertyTitle || "Titre de votre site",
      text: shareText,
      url: shareUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        console.log("Contenu partage avec succes !");
        return;
      }

      await copyToClipboard(shareUrl);
      setIsShareSuccessVisible(true);
    } catch (error) {
      console.log("Erreur ou partage annule :", error);
      setIsShareSuccessVisible(false);
    }
  };

  return (
    <section className="bg-bleu-950 pb-6 md:pb-21 flex flex-col gap-5 md:gap-10">
      <div className="md:hidden">
        <PicturesMobile pictures={pictures} state={state} date_sold={date_sold} date_rented={date_rented} />
      </div>

      <div className="hidden md:block">
        <PicturesDesktop pictures={pictures} state={state} date_sold={date_sold} date_rented={date_rented} />
      </div>
      <div className="framing padding-x flex flex-col gap-11 md:gap-20">
        <div className="flex flex-col gap-2 text-bleu-50">
          <div className="flex flex-col lg:flex-row-reverse items-end lg:items-start justify-between gap-2">

            <button
              type="button"
              onClick={handleShareClick}
              className="lg:w-fit body-s underline hover:bg-bleu-900 px-2 py-1 rounded-full cursor-pointer"
            >
              Partager
            </button>
            {propertyTitle && <h1 className="h4-h3">{propertyTitle}</h1>}
          </div>
          {propertyLocation && <p className="h5">{propertyLocation}</p>}
        </div>
        {propertyInfoCount > 0 && (
          <div
            className={`grid grid-cols-2 gap-2 ${propertyInfoDesktopGridClass} [&>*:last-child:nth-child(odd)]:col-span-2 lg:[&>*:last-child:nth-child(odd)]:col-span-1`}
          >
            {propertyInfoItems.map(({ key, label, value, icon, iconOffset }) => (
              <PropertyInfoCard
                key={key}
                label={label}
                value={value}
                icon={icon}
                className={propertyInfoCardClass}
                iconOffset={iconOffset}
              />
            ))}
          </div>
        )}

        {isShareSuccessVisible && (
          <div className="fixed bottom-6 left-1/2 z-120 -translate-x-1/2 rounded-full bg-bleu-700 px-4 py-2 text-bleu-50 shadow-lg body-s flex items-center gap-2">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_191_3331)">
                <path d="M15 20H5C3.67441 19.9984 2.40356 19.4711 1.46622 18.5338C0.528882 17.5964 0.00158786 16.3256 0 15L0 5C0.00158786 3.67441 0.528882 2.40356 1.46622 1.46622C2.40356 0.528882 3.67441 0.00158786 5 0L15 0C16.3256 0.00158786 17.5964 0.528882 18.5338 1.46622C19.4711 2.40356 19.9984 3.67441 20 5V15C19.9984 16.3256 19.4711 17.5964 18.5338 18.5338C17.5964 19.4711 16.3256 19.9984 15 20ZM5 2C4.20435 2 3.44129 2.31607 2.87868 2.87868C2.31607 3.44129 2 4.20435 2 5V15C2 15.7956 2.31607 16.5587 2.87868 17.1213C3.44129 17.6839 4.20435 18 5 18H15C15.7956 18 16.5587 17.6839 17.1213 17.1213C17.6839 16.5587 18 15.7956 18 15V5C18 4.20435 17.6839 3.44129 17.1213 2.87868C16.5587 2.31607 15.7956 2 15 2H5ZM24 19V6C24 5.73478 23.8946 5.48043 23.7071 5.29289C23.5196 5.10536 23.2652 5 23 5C22.7348 5 22.4804 5.10536 22.2929 5.29289C22.1054 5.48043 22 5.73478 22 6V19C22 19.7956 21.6839 20.5587 21.1213 21.1213C20.5587 21.6839 19.7956 22 19 22H6C5.73478 22 5.48043 22.1054 5.29289 22.2929C5.10536 22.4804 5 22.7348 5 23C5 23.2652 5.10536 23.5196 5.29289 23.7071C5.48043 23.8946 5.73478 24 6 24H19C20.3256 23.9984 21.5964 23.4711 22.5338 22.5338C23.4711 21.5964 23.9984 20.3256 24 19Z" fill="#EFF7FB" />
              </g>
              <defs>
                <clipPath id="clip0_191_3331">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <span>Lien copié dans le presse-papiers</span>
          </div>
        )}
      </div>
    </section>
  );
};

export default PropertyHero;

